import { useCallback, useEffect, useState } from 'react'

import { IconBotId, IconBrandOpenai, IconExternalLink, IconKey } from '@tabler/icons-react'

import { type Account, AccountInclusionStatus, type Application, type Connection } from '@subaio/api-types/financedata'
import { ButtonGroup, FormattedMessage, useAsyncResource, useNavigation } from '@subaio/components'
import { handleLogin } from '@subaio/psd2login-web'
import { logger, tokenDecode } from '@subaio/utils'

import { asContainer } from 'lib/container'
import { isB2C } from 'lib/userAuth'

import { LinkSolidNative } from 'components/Buttons'
import { IconChatGpt, IconClaude, IconLovable } from 'components/Icons'
import { CenterContent } from 'components/Layout'
import { useSnackbar } from 'components/Snackbars'
import { Spinner } from 'components/Spinner'

import { Connections } from './Connections'
import { ConnectWrapper } from './ConnectWrapper'
import { ContextCard } from './ContextCard'
import { ContextWrapper } from './ContextWrapper'
import { IconRow } from './IconRow'
import { LinkDoumentation } from './LinkDocumentation'
import { LoadingWrapper } from './LoadingWrapper'
import { StaticField } from './StaticField'
import { Tabs } from './Tabs'
import { Timeline } from './Timeline'

type State = {
	application: Application
	demoApplication: Application | undefined
}

export const container = asContainer<State>()({
	path: '/',
	authentication: { type: 'public' },
	initialize: async ({ updatePage, userAuth, api }) => {
		if (userAuth.status !== 'authenticated') updatePage({ name: 'Login' })

		const { billingStatus } = await api.get<{ billingStatus: string; billingUrl: string }>('/billing/status')
		if (billingStatus !== 'ACTIVE') updatePage({ name: 'Billing' })

		const { applications } = await api.get<{ applications: Array<Application> }>('/applications')

		const demoApplication = applications.find((application) => application.type === 'SANDBOX')
		const application = (applications.find((application) => application.type === 'PRODUCTION') ?? demoApplication)!

		return {
			application,
			demoApplication,
		}
	},
	Running: ({ application, demoApplication, userAuth, api }) => {
		const { onUpdateUrlTo } = useNavigation()
		const [selectedApplication, setSelectedApplication] = useState(application)
		const { showSnackbar } = useSnackbar()
		const externalUserReference =
			userAuth.status === 'authenticated' ? tokenDecode(userAuth.user.token).external_user_reference : undefined

		const connectionsResource = useAsyncResource(
			useCallback(async () => {
				const { connections } = await api.get<{ connections: Array<Connection> }>(
					`/applications/${selectedApplication.id}/connections`,
				)

				if (connections.length === 0) {
					return {
						connections,
						accounts: [],
					}
				}

				const { accounts } = await api.get<{ accounts: Array<Account> }>(
					`/applications/${selectedApplication.id}/accounts`,
				)

				return {
					connections,
					accounts,
				}
			}, [api, selectedApplication]),
		)

		const removeConnection = useCallback(
			async (connectionId: string) => {
				await api.delete(`/applications/${selectedApplication.id}/connections/${connectionId}`)
				if (connectionsResource.status === 'done') connectionsResource.refresh()
			},
			[api, selectedApplication, connectionsResource],
		)

		const getClientSecret = useCallback(
			async () =>
				(await api.get<{ clientSecret: string }>(`/applications/${selectedApplication.id}/credentials`)).clientSecret,
			[api, selectedApplication],
		)

		const updateAccountInclusion = useCallback(
			async (accountsMap: Record<string, boolean>) => {
				const accounts = Object.entries(accountsMap).map(([id, value]) => ({
					id,
					inclusionStatus: value ? AccountInclusionStatus.IncludedByUser : AccountInclusionStatus.ExcludedByUser,
				}))

				const result = await api.patch<{ accounts: Array<Account> }>(
					`/applications/${selectedApplication.id}/accounts/selections`,
					{ accounts },
				)
				if (connectionsResource.status === 'done')
					connectionsResource.set((value) => ({ ...value, accounts: result.accounts }))

				showSnackbar({ id: 'account-selection-update', message: 'Updated your account selections' })
			},
			[api, selectedApplication, connectionsResource, showSnackbar],
		)

		const openCollectionFlow = useCallback(async () => {
			const { flows } = await api.post<{ flows: Array<{ applicationUrl: string }> }>(
				`/applications/${selectedApplication.id}/data-collection-flows`,
				{},
			)

			try {
				await handleLogin({ url: flows[0].applicationUrl })
			} catch (error) {
				logger.error(error)
			}

			if (connectionsResource.status === 'done') connectionsResource.refresh()
		}, [api, selectedApplication, connectionsResource])

		useEffect(() => {
			if (userAuth.status !== 'authenticated') {
				onUpdateUrlTo({ name: 'NotAuthenticated' })
			}
		}, [userAuth, onUpdateUrlTo])

		return (
			<CenterContent topMargin={false}>
				<h1>
					<FormattedMessage id='home.title' defaultMessage='Build with Finance Data' />
				</h1>
				<p>
					<FormattedMessage
						id='home.description'
						defaultMessage='Choose whether to work with your own financial data or explore Finance Data using our demo data and predefined personas.'
					/>
				</p>
				<ConnectWrapper hasConnections={connectionsResource.value?.connections.length !== 0}>
					{demoApplication && (
						<Tabs
							isDemoData={selectedApplication.type === 'SANDBOX'}
							onChange={(isDemoData) =>
								isDemoData ? setSelectedApplication(demoApplication) : setSelectedApplication(application)
							}
						/>
					)}
					<section
						id='home-data-panel'
						role='tabpanel'
						aria-labelledby={selectedApplication.type === 'SANDBOX' ? 'home-demo-data-tab' : 'home-your-data-tab'}
					>
						{connectionsResource.status === 'loading' && (
							<LoadingWrapper>
								<Spinner />
							</LoadingWrapper>
						)}
						{connectionsResource.status === 'errored' && <p>Failed to get your bank connections/accounts</p>}
						{connectionsResource.status === 'done' && connectionsResource.value.connections.length === 0 && (
							<Timeline isDemoData={selectedApplication.type === 'SANDBOX'} openCollectionFlow={openCollectionFlow} />
						)}
						{connectionsResource.status === 'done' && connectionsResource.value.connections.length > 0 && (
							<Connections
								isDemoData={selectedApplication.type === 'SANDBOX'}
								connections={connectionsResource.value.connections}
								accounts={connectionsResource.value.accounts}
								updateAccountInclusion={updateAccountInclusion}
								openCollectionFlow={openCollectionFlow}
								removeConnection={removeConnection}
							/>
						)}
					</section>
				</ConnectWrapper>
				{connectionsResource.status === 'done' && (
					<ContextWrapper>
						{userAuth.status === 'authenticated' && !isB2C(userAuth.user.token) && (
							<ContextCard
								title={<FormattedMessage id='home.via.apiIntegration.title' defaultMessage='Via API integration' />}
								icon={<IconKey />}
							>
								{connectionsResource.value?.connections.length === 0 ? (
									<p>
										{selectedApplication.type === 'SANDBOX' ? (
											<FormattedMessage
												id='home.demo.via.apiIntegration.description'
												defaultMessage="Once your demo persona is ready, you'll receive API credentials to explore its financial data in your own product."
											/>
										) : (
											<FormattedMessage
												id='home.via.apiIntegration.description'
												defaultMessage="Once connected, you'll receive API credentials to securely access your financial data in your own product."
											/>
										)}
									</p>
								) : (
									<>
										<FormattedMessage
											id='home.via.apiIntegration.connected.description'
											defaultMessage='Use your API credentials to access and work with your financial data in your own code.'
										/>
										<StaticField
											label={<FormattedMessage id='home.via.apiIntegration.labelClientId' defaultMessage='Client ID' />}
											value={selectedApplication.clientId}
										/>
										{typeof externalUserReference === 'string' && (
											<StaticField
												label={
													<FormattedMessage
														id='home.via.apiIntegration.labelExternalReferenceId'
														defaultMessage='External reference ID'
													/>
												}
												value={externalUserReference}
											/>
										)}
										<StaticField
											label={
												<FormattedMessage
													id='home.via.apiIntegration.labelClientSecret'
													defaultMessage='Client secret'
												/>
											}
											value={getClientSecret}
										/>
										<ButtonGroup center>
											<LinkDoumentation
												target='_blank'
												rel='noopener'
												href='https://developer.subaio.com/apis/finance-data-api/v1'
											>
												<FormattedMessage
													id='home.via.apiIntegration.labelDocumentationLink'
													defaultMessage='View API documentation'
												/>
												<IconExternalLink />
											</LinkDoumentation>
										</ButtonGroup>
									</>
								)}
							</ContextCard>
						)}
						<ContextCard
							title={<FormattedMessage id='home.via.mcp.title' defaultMessage='Via MCP' />}
							icon={<IconBotId />}
							aside={
								<IconRow>
									<IconLovable />
									<IconChatGpt />
									<IconClaude />
								</IconRow>
							}
						>
							{connectionsResource.value?.connections.length === 0 ? (
								<p>
									{selectedApplication.type === 'SANDBOX' ? (
										<FormattedMessage
											id='home.demo.via.mcp.description'
											defaultMessage="Once you've chosen a persona, add Finance Data to tools like ChatGPT, Claude, and Lovable to explore the demo data with AI."
										/>
									) : (
										<FormattedMessage
											id='home.via.mcp.description'
											defaultMessage='Once your bank is connected, add Finance Data to tools like ChatGPT, Claude, and Lovable so you can work with your financial data through AI.'
										/>
									)}
								</p>
							) : (
								<>
									<div>
										<p>
											<FormattedMessage
												id='home.via.mcp.connected.description'
												defaultMessage='Connect Finance Data to AI tools like ChatGPT, Claude, and Lovable.'
											/>
										</p>
										<ButtonGroup column center spacing={12}>
											<LinkSolidNative
												target='_blank'
												rel='noopener'
												href='https://developer.subaio.com/docs/finance-data/mcp/lovable'
												size='small'
												label={
													<>
														<IconLovable style={{ height: '20px' }} />
														<FormattedMessage
															id='home.via.mcp.buttonConnectLoveable'
															defaultMessage='Connect with Loveable'
														/>
														<IconExternalLink strokeWidth={1.2} />
													</>
												}
												type='dark'
											/>
											<LinkSolidNative
												target='_blank'
												rel='noopener'
												href='https://developer.subaio.com/docs/finance-data/mcp/codex'
												size='small'
												label={
													<>
														<IconBrandOpenai style={{ color: 'white' }} />
														<FormattedMessage
															id='home.via.mcp.buttonConnectCodex'
															defaultMessage='Connect with Codex'
														/>
														<IconExternalLink strokeWidth={1.2} />
													</>
												}
												type='dark'
											/>
											<LinkSolidNative
												target='_blank'
												rel='noopener'
												href='https://developer.subaio.com/docs/finance-data/mcp/claude-code'
												size='small'
												label={
													<>
														<IconClaude style={{ color: 'white', height: '22px' }} />
														<FormattedMessage
															id='home.via.mcp.buttonConnectClaude'
															defaultMessage='Connect with Claude'
														/>
														<IconExternalLink strokeWidth={1.2} />
													</>
												}
												type='dark'
											/>
										</ButtonGroup>
									</div>
									<ButtonGroup center>
										<LinkDoumentation
											target='_blank'
											rel='noopener'
											href='https://developer.subaio.com/docs/finance-data/mcp'
										>
											<FormattedMessage
												id='home.via.mcp.labelDocumentationLink'
												defaultMessage='View MCP setup guides'
											/>
											<IconExternalLink />
										</LinkDoumentation>
									</ButtonGroup>
								</>
							)}
						</ContextCard>
					</ContextWrapper>
				)}
			</CenterContent>
		)
	},
})
